import { memo, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import {
  qcTaskAutocompleteListboxSlotProps,
  qcTaskDropdownSx,
  qcTaskFilterBarPaperSx,
} from '../styles/qcTaskFilterBarStyles';

/**
 * Workflow + batch pickers for MT-QC-TASK-001.
 * Memoized so parent re-renders (e.g. table pagination) do not recreate Autocomplete trees unnecessarily.
 */
function QcTaskWorkflowBatchFiltersComponent({
  activeTab,
  workflowValue,
  batchValue,
  workflowOptions,
  batchOptions,
  workflowId,
  isWorkflowLoading,
  isWorkflowFetching,
  isBatchesLoading,
  isBatchesFetching,
  onWorkflowChange,
  onBatchChange,
  /** Optional override for the workflow Autocomplete option label. */
  getWorkflowOptionLabel: getWorkflowOptionLabelProp,
}) {
  const defaultGetWorkflowOptionLabel = useCallback(
    (option) =>
      `${option?.workflow_id ?? ''} (Batches: ${option?.total_batch_count ?? 0})`,
    [],
  );
  const getWorkflowOptionLabel = getWorkflowOptionLabelProp ?? defaultGetWorkflowOptionLabel;

  const getBatchOptionLabel = useCallback(
    (option) =>
      `${option?.batch_name ?? option?.batch_id ?? ''} (Req: ${option?.total_requests_count ?? 0})`,
    [],
  );

  const isWorkflowOptionEqual = useCallback(
    (option, value) => option?.workflow_id === value?.workflow_id,
    [],
  );

  const isBatchOptionEqual = useCallback(
    (option, value) => option?.batch_id === value?.batch_id,
    [],
  );

  return (
    <Paper elevation={0} sx={qcTaskFilterBarPaperSx}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 600,
          color: 'text.secondary',
          fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.875rem' },
          mb: { xs: 0.5, sm: 0.75, md: 1.5 },
          letterSpacing: { xs: '0.01em', md: '0.02em' },
          lineHeight: 1.3,
        }}
      >
        Workflow &amp; Batch
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr', md: '1fr 1fr' },
          gap: { xs: 1, sm: 1.25, md: 2 },
        }}
      >
        <Autocomplete
          key={`wf-${activeTab}`}
          id="workflow-dropdown"
          fullWidth
          getOptionLabel={getWorkflowOptionLabel}
          value={workflowValue}
          onChange={(_, newValue) => onWorkflowChange(newValue)}
          options={workflowOptions}
          loading={isWorkflowLoading || isWorkflowFetching}
          disabled={isWorkflowLoading}
          isOptionEqualToValue={isWorkflowOptionEqual}
          noOptionsText="No Workflow Assigned"
          sx={qcTaskDropdownSx}
          slotProps={{
            listbox: qcTaskAutocompleteListboxSlotProps,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                workflowOptions.length > 0
                  ? 'Select Workflow'
                  : 'No Workflow Assigned'
              }
              size="small"
              placeholder="Choose workflow"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isWorkflowLoading || isWorkflowFetching ? (
                      <Box
                        sx={{
                          pr: { xs: 0.5, sm: 1 },
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <CircularProgress size={16} thickness={5} />
                      </Box>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          key={`batch-${activeTab}-${workflowId || 'none'}`}
          id="batch-dropdown"
          fullWidth
          getOptionLabel={getBatchOptionLabel}
          value={batchValue}
          onChange={(_, newValue) => onBatchChange(newValue)}
          options={batchOptions}
          loading={isBatchesLoading || isBatchesFetching}
          disabled={isBatchesLoading || !workflowId}
          isOptionEqualToValue={isBatchOptionEqual}
          noOptionsText="No Batch Assigned"
          sx={qcTaskDropdownSx}
          slotProps={{
            listbox: qcTaskAutocompleteListboxSlotProps,
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={
                batchOptions.length > 0 ? 'Select Batch' : 'No Batch Assigned'
              }
              size="small"
              placeholder="Choose batch"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isBatchesLoading || isBatchesFetching ? (
                      <Box
                        sx={{
                          pr: { xs: 0.5, sm: 1 },
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <CircularProgress size={16} thickness={5} />
                      </Box>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Box>
    </Paper>
  );
}

const QcTaskWorkflowBatchFilters = memo(QcTaskWorkflowBatchFiltersComponent);
export default QcTaskWorkflowBatchFilters;
